import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { XCircle, Printer, ReceiptText, Settings, CheckCircle, MessageSquare } from 'lucide-react'
import { OrderStatusBadge } from './OrderStatusBadge'
import { OrderTypeBadge } from './OrderTypeBadge'
import { PaymentStatusBadge } from './PaymentStatusBadge'
import { CustomerInfo } from './CustomerInfo'
import { OrderItemsList } from './OrderItemsList'
import { PricingBreakdown } from './PricingBreakdown'
import { StatusChangeButtons } from './StatusChangeButtons'
import { ReceiptPreview } from './ReceiptPreview'
import { PrinterSettingsDialog } from './PrinterSettingsDialog'
import { CustomStatusCommentDialog } from './CustomStatusCommentDialog'
import { cn } from '@/lib/utils'
import { getOrderType, calculateTotal, formatTime, formatDateTime } from '../utils'
import { usePrintOrder, canPrint, useVariationCategoryLabels } from '../hooks/usePrintOrder'
import { useCustomerStatusT } from '../hooks/useCustomerStatusT'
import { useResolvedPlaces } from '@/features/maps/hooks/useResolvedPlaces'
import { toastPrinterSuccess, toastPrinterError } from '../utils/hardwareToasts'
import { formatCurrency } from '@/lib/formatters'
import { Clock, Timer } from 'lucide-react'
import type { DeepOrder } from '../types'
import { PosOrderPayment } from '@/features/pos-ordering/PosOrderPayment'

/** RestaurantApp preset times: 15, 30, 45, 60, 75, 90, 100, 120 */
const PRESET_MINUTES = [15, 30, 45, 60, 75, 90, 100, 120]

export interface OrderDetailContentProps {
  order: DeepOrder
  orderConfirmationEnabled?: boolean
  onStatusChange: (orderId: number, status: string) => void
  onCancelOrder: () => void
  onConfirmOrder?: () => void
  onDeclineOrder?: () => void
  /** Optional: called after a time-preset or "complete" button is tapped, to dismiss the detail view. */
  onAfterTimeAction?: () => void
  isUpdating?: boolean
  isConfirming?: boolean
  isDeclining?: boolean
}

export function OrderDetailContent({
  order,
  orderConfirmationEnabled = false,
  onStatusChange,
  onCancelOrder,
  onConfirmOrder,
  onDeclineOrder,
  onAfterTimeAction,
  isUpdating = false,
  isConfirming = false,
  isDeclining = false,
}: OrderDetailContentProps) {
  const { t } = useTranslation('orders')
  const customerT = useCustomerStatusT()
  const { t: posT } = useTranslation('posOrdering')
  const isPos = order.userId.startsWith('pos:')
  const needsConfirmation = orderConfirmationEnabled && order.confirmationStatus === 'pending' && order.cancelStatus !== 'cancelled'
  const isCancelled = order.cancelStatus === 'cancelled'
  const isDelivery = order.withDelivery
  const orderType = getOrderType(order)
  const total = calculateTotal(order)

  const { isPrinting, printOrder, buildReceiptElements } = usePrintOrder()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isPrinterSettingsOpen, setIsPrinterSettingsOpen] = useState(false)
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false)

  const deliveryPlaceId = isDelivery ? order.deliveryAddress?.city : undefined
  const deliveryPlaceIds = useMemo(
    () => (deliveryPlaceId ? [deliveryPlaceId] : []),
    [deliveryPlaceId],
  )
  const resolvedPlaces = useResolvedPlaces(deliveryPlaceIds)
  const resolvedAddress = deliveryPlaceId
    ? resolvedPlaces[deliveryPlaceId]?.formattedAddress ?? undefined
    : undefined
  const variationCategoryLabels = useVariationCategoryLabels(order)
  const receiptElements = !isDelivery || resolvedAddress
    ? buildReceiptElements(order, resolvedAddress, { variationCategoryLabels, preview: true })
    : null

  const handlePrint = async () => {
    try {
      await printOrder(order, resolvedAddress)
      toastPrinterSuccess(t)
    } catch {
      toastPrinterError(t)
    }
  }

  return (
    <div className="space-y-6">
      {/* Order header */}
      <div>
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-2xl sm:text-3xl font-bold">{t('order.orderId', { id: order.id })}</h2>
          <span className="text-2xl sm:text-3xl font-bold text-primary">{formatCurrency(total)}</span>
        </div>

        {/* Time info */}
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-2">
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span>{formatDateTime(order.date)}</span>
          </div>
          {order.commitedTime && (
            <div className={cn(
              'flex items-center gap-1',
              order.commitedTime < Date.now() ? 'text-status-error' : 'text-status-warning'
            )}>
              <Timer className="h-3.5 w-3.5" />
              <span>{t('order.scheduledFor')}: {formatTime(order.commitedTime)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap items-center gap-3">
        <OrderStatusBadge status={order.status} cancelStatus={order.cancelStatus} />
        <OrderTypeBadge type={orderType} />
        {!order.posRestaurantId && <PaymentStatusBadge paymentStatus={order.paymentStatus} refundStatus={order.refundStatus} />}
      </div>

      {order.posRestaurantId && <PosOrderPayment key={`${order.posRestaurantId}:${order.id}`}
        restaurantId={order.posRestaurantId} orderId={order.id} cancelled={isCancelled} />}

      {/* Cancel reason banner */}
      {isCancelled && order.cancelReason && (
        <div className="p-3 bg-status-error/10 border border-status-error/20 rounded-md">
          <p className="text-sm text-status-error">
            <strong>{t('cancel.reason')}:</strong> {order.cancelReason}
          </p>
        </div>
      )}

      {/* Print action bar — large touch targets for kitchen use */}
      <div className="flex items-stretch gap-3">
        {canPrint && (
          <Button
            onClick={() => void handlePrint()}
            disabled={isPrinting}
            className="flex-1 h-14 sm:h-16 text-base sm:text-lg font-bold rounded-xl gap-3 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all"
          >
            <Printer className="h-5 w-5 sm:h-6 sm:w-6" />
            {isPrinting
              ? t('actions.printing', 'Druckt...')
              : isPos ? posT('payment.printKitchenTicket') : t('actions.print', 'Bon drucken')}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => setIsPreviewOpen(true)}
          className="h-14 sm:h-16 px-4 sm:px-5 rounded-xl gap-2 border-2 border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
        >
          <ReceiptText className="h-5 w-5" />
          <span className="text-sm sm:text-base font-semibold">{isPos ? posT('payment.previewKitchenTicket') : t('actions.previewReceipt', 'Bon Vorschau')}</span>
        </Button>
        {canPrint && (
          <Button
            variant="secondary"
            size="icon"
            onClick={() => setIsPrinterSettingsOpen(true)}
            className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl border-2 border-border shrink-0"
            title={t('printer.settingsTitle')}
          >
            <Settings className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Confirm/Decline — only when orderConfirmationEnabled and order is pending */}
      {needsConfirmation && onConfirmOrder && (
        <>
          <div className="flex gap-3">
            <Button
              variant="default"
              onClick={onConfirmOrder}
              disabled={isConfirming}
              className="flex-1 h-14 text-lg font-bold rounded-xl"
            >
              {t('confirmation.confirmOrder')}
            </Button>
            {onDeclineOrder && (
              <Button
                variant="ghost"
                onClick={onDeclineOrder}
                disabled={isDeclining}
                className="h-14 px-6 text-lg rounded-xl text-muted-foreground hover:text-status-error hover:bg-status-error/10"
              >
                <XCircle className="h-5 w-5 me-2" />
                {t('confirmation.decline')}
              </Button>
            )}
          </div>
          <Separator />
        </>
      )}

      {/* Time presets — calls editOrderStatus with status text (like RestaurantApp) */}
      {!isCancelled && order.status.toLowerCase() !== 'completed' && (
        <>
          <div className="space-y-4">
            <h3 className="text-lg font-bold uppercase tracking-wide">
              {isDelivery
                ? t('kanban.deliveryIn')
                : t('kanban.pickupIn')}
            </h3>
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              {PRESET_MINUTES.map((minutes) => {
                const statusText = isDelivery
                  ? customerT('kanban.deliveryStatusText', { minutes })
                  : customerT('kanban.pickupStatusText', { minutes })
                return (
                  <Button
                    key={minutes}
                    variant="secondary"
                    onClick={() => {
                      onStatusChange(order.id, statusText)
                      onAfterTimeAction?.()
                    }}
                    disabled={isUpdating}
                    className="h-16 sm:h-20 text-xl sm:text-2xl font-bold rounded-xl border-2 border-border hover:bg-primary hover:text-primary-foreground hover:border-primary active:bg-primary/90 transition-colors"
                  >
                    {minutes}
                  </Button>
                )
              })}
            </div>
            {/* Custom status comment (from old RestaurantApp "Kommentar" button) */}
            <Button
              variant="secondary"
              onClick={() => setIsCommentDialogOpen(true)}
              disabled={isUpdating}
              className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold rounded-xl border-2 border-border"
            >
              <MessageSquare className="h-5 w-5 me-2" />
              {t('kanban.comment')}
            </Button>
            {/* "Abholbereit" / "Lieferung unterwegs" — single-tap order complete (from old RestaurantApp) */}
            <Button
              onClick={() => {
                onStatusChange(order.id, isDelivery
                  ? customerT('kanban.deliveryCompleteStatusText')
                  : customerT('kanban.pickupCompleteStatusText'))
                onAfterTimeAction?.()
              }}
              disabled={isUpdating}
              className="w-full h-14 sm:h-16 text-lg sm:text-xl font-bold rounded-xl bg-emerald-700 text-white hover:bg-emerald-800 active:bg-emerald-900 transition-colors"
            >
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 me-3" />
              {isDelivery
                ? t('kanban.deliveryCompleteLabel')
                : t('kanban.pickupCompleteLabel')}
            </Button>
          </div>
          <Separator />
        </>
      )}

      {/* Customer info */}
      <CustomerInfo order={order} />
      <Separator />

      {/* Items */}
      <OrderItemsList items={order.orderItems} />
      <Separator />

      {/* Pricing */}
      <PricingBreakdown order={order} />

      {/* Status actions + cancel — large, color-coded */}
      {!isCancelled && (
        <>
          <Separator />
          <div className="space-y-3">
            <StatusChangeButtons
              order={order}
              onStatusChange={(status) => onStatusChange(order.id, status)}
              isLoading={isUpdating}
            />
            {/* Destructive action: outlined red — clearly visible AND clearly dangerous,
                placed at the bottom away from frequent actions to discourage misclicks. */}
            {order.status.toLowerCase() !== 'completed' && !order.posPayment && !order.posPaymentUnavailable && (
              <div className="pt-4 mt-2">
                <Button
                  variant="outline"
                  onClick={onCancelOrder}
                  disabled={isUpdating}
                  className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold rounded-xl border-2 border-status-error/40 text-status-error hover:bg-status-error/10 hover:border-status-error hover:text-status-error"
                >
                  <XCircle className="w-5 h-5 me-2" />
                  {t('actions.cancel')}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Receipt preview dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-sm overflow-y-auto p-0 bg-muted">
          {receiptElements ? (
            <ReceiptPreview elements={receiptElements} />
          ) : (
            <p className="p-8 text-center text-sm text-muted-foreground">{t('loading.title')}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Printer settings dialog */}
      <PrinterSettingsDialog isOpen={isPrinterSettingsOpen} onClose={() => setIsPrinterSettingsOpen(false)} />

      {/* Custom status comment dialog */}
      <CustomStatusCommentDialog
        isOpen={isCommentDialogOpen}
        onClose={() => setIsCommentDialogOpen(false)}
        onSubmit={(comment) => {
          onStatusChange(order.id, comment)
          setIsCommentDialogOpen(false)
        }}
        isLoading={isUpdating}
      />
    </div>
  )
}
